<script setup lang="ts">
  import { onMounted, reactive, ref, watch } from "vue";
  import { useRouter } from "vue-router";
  import HelpData from "~/assets/data/help-result-v2";
  import noSearchResultImage from "~/assets/images/empty/no-search-result.png";
  import Common from "~/utils/common";

  interface SuggestItem {
    id: string;
    title: string;
    content: string;
    [key: string]: any;
  }

  const props = defineProps({
    modelValue: {
      type: String,
      default: "",
    },
    getPopupContainer: {
      type: Function,
      default: () => document.body,
    },
  });

  const router = useRouter();
  const visible = ref(false);
  const style = reactive({});
  const suggestList = ref<SuggestItem[]>([]);
  const list = ref<SuggestItem[]>([]);
  const suggestInput = ref<HTMLElement>();

  // 监听输入值变化
  watch(
    () => props.modelValue,
    (val) => {
      reset();
      if (val) {
        search(val);
      }
    },
  );

  // 监听下拉框显示状态
  watch(
    () => visible.value,
    (val) => {
      if (val) {
        resize();
      }
    },
  );

  // 初始化数据
  onMounted(() => {
    list.value = Common.clone(Common.treeToArray(HelpData).filter((item: any) => item.content));
    for (const item of list.value) {
      if (item.content) {
        const elem = document.createElement("div");
        elem.innerHTML = item.content;
        item.content = elem.innerText;
      }
    }
  });

  // 跳转到目标页面
  const onToTarget = (item: SuggestItem) => {
    router.push({
      path: `/help/result`,
      hash: `#${item.id}`,
    });
  };

  // 搜索处理
  const search = (keyword: string) => {
    if (!keyword.trim()) return;
    const data = Common.clone(list.value);
    suggestList.value = data.filter((item: SuggestItem) => {
      const { title, content } = item;
      if (title.includes(keyword) || content.includes(keyword)) {
        const reg = new RegExp(keyword, "g");
        item.title = title.replace(reg, `<span class="high">${keyword}</span>`);
        const text = Common.textExtract(content, keyword);
        item.content = text.replace(reg, `<span class="high">${keyword}</span>`);
        return true;
      }
      return false;
    });
    visible.value = true;
  };

  // 调整宽度
  const resize = () => {
    if (suggestInput.value) {
      const { offsetWidth } = suggestInput.value;
      Object.assign(style, { width: `${offsetWidth}px` });
    }
  };

  // 重置状态
  const reset = () => {
    suggestList.value = [];
  };
</script>

<template>
  <a-dropdown
    v-model:open="visible"
    :trigger="['click']"
    :get-popup-container="getPopupContainer as any"
    overlay-class-name="help-suggest">
    <div ref="suggestInput" class="suggest-input">
      <slot></slot>
    </div>
    <template #overlay>
      <div v-if="modelValue" class="help-suggest-overlay" :style="style">
        <template v-if="suggestList.length > 0">
          <div class="suggest-header">
            <span>共找到</span>
            <strong> {{ suggestList.length }} </strong>
            <span>条搜索结果</span>
          </div>
          <div class="suggest-items">
            <div v-for="(item, i) in suggestList" :key="i" class="suggest-item" @click.stop="onToTarget(item)">
              <div class="suggest-title" v-html="item.title"></div>
              <div class="suggest-desc" v-html="item.content"></div>
            </div>
          </div>
        </template>
        <div v-else class="suggest-empty">
          <a-empty :image="noSearchResultImage" description="抱歉，未搜索到内容" />
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<style lang="less">
  .help-suggest {
    z-index: 10;
  }

  .help-suggest-overlay {
    max-height: 450px;
    padding: 20px;
    padding-left: 10px;
    overflow-y: auto;
    background: #fff;
    box-shadow: 0 0 10px 0 rgba(63, 74, 86, 30%);

    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #b6c4d1;
      border-radius: 5px;
    }

    &::-webkit-scrollbar-track {
      background: #f8f8f8;
      border-radius: 0;
    }
  }
</style>
<style lang="less" scoped>
  .suggest-empty {
    padding: 50px 0;

    :deep(.ant-empty) {
      .ant-empty-description {
        font-size: 16px;
      }
    }
  }

  .suggest-header {
    padding-top: 12px;
    padding-left: 10px;
    margin-bottom: 20px;
    font-size: 14px;
    color: #8c95a8;
  }

  .suggest-items {
    .suggest-item {
      padding: 6px 10px 20px;
      cursor: pointer;
      transition: ease 300ms all;

      &:hover {
        background: #f5f5f5;
      }

      .suggest-title {
        margin-bottom: 15px;
        font-size: 16px;
        color: #333;
      }

      .suggest-desc {
        font-size: 14px;
        line-height: 22px;
        color: #666;
        word-break: break-all;
      }

      :deep(.high) {
        color: var(--primary-color);
      }
    }
  }
</style>
